import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { App as MyApp, AppRoutes } from './app';
import { unregister } from './serviceWorker';
import './i18n';
import './styles/index.scss';
import configureAmplify from 'aws.config.exports';
import { setDefaultOptions } from 'esri-loader';
import {
  initializeBasemapTokenInterceptors,
  setBasemapSessionToken
} from 'utilities/basemap-token-interceptor.util';
import {
  getStoredSession,
  isSessionValid
} from 'utilities/basemap-session.util';
import { debugLog, debugError } from 'utilities/basemap-debug.util';

setDefaultOptions({ version: '4.33', css: true });

// Initialize basemap token interceptors and restore/create session
initializeBasemapTokenInterceptors();
var storedSession = getStoredSession();
if (storedSession && isSessionValid(storedSession)) {
  debugLog('Restoring basemap session from localStorage');
  setBasemapSessionToken(storedSession.sessionToken);
} else {
  debugLog('No valid basemap session found, creating new session...');
  // Import createBasemapSession dynamically to avoid circular dependencies
  import('utilities/basemap-session.util')
    .then(function (module) {
      var createBasemapSession = module.createBasemapSession;
      var apiKey = process.env.REACT_APP_ESRI_API_KEY;
      if (!apiKey) {
        throw new Error('ArcGIS API key is not set');
      }
      return createBasemapSession(apiKey);
    })
    .then(function (session) {
      debugLog('Basemap session created successfully in index.tsx');
      setBasemapSessionToken(session.sessionToken);
    })
    .catch(function (error) {
      debugError('Failed to create basemap session in index.tsx:', error);
    });
}

// Force load ArcGIS CSS in iframe context
if (window.self !== window.top) {
  // Check if ArcGIS CSS is already loaded
  const existingCSS = document.querySelector('link[href*="arcgis"]');
  if (!existingCSS) {
    // Force load ArcGIS CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://js.arcgis.com/4.33/esri/themes/light/main.css';
    document.head.appendChild(link);
  }
}

const AWSCONFIG = async () => await configureAmplify();
AWSCONFIG();

// Global handler for unhandled promise rejections from ArcGIS API
// This prevents the app from crashing when ArcGIS internal promises are rejected
// after the map view has been destroyed (e.g., during navigation)
window.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    const error = event.reason;
    // Check if this is an ArcGIS-related error that we can safely ignore
    if (
      error &&
      (error.message?.includes(
        "Cannot read properties of null (reading 'reject')"
      ) ||
        error.message?.includes(
          "Cannot read properties of null (reading 'resolve')"
        ) ||
        error.message?.includes('_deferred is null') ||
        error.message?.includes('AbortError') ||
        error.name === 'AbortError')
    ) {
      // Prevent the error from being logged to console as an unhandled rejection
      event.preventDefault();
      debugLog(
        '[ArcGIS] Suppressed expected promise rejection during navigation:',
        error?.message
      );
    }
  }
);

const Root = () => (
  <React.StrictMode>
    <MyApp>
      <Router>
        <AppRoutes />
      </Router>
    </MyApp>
  </React.StrictMode>
);

ReactDOM.render(<Root />, document.getElementById('root'));

unregister();
